import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useState, type ReactNode } from 'react';

interface SaveableFieldProps {
    children: ReactNode;
    originalValue: string | number;
    currentValue: string | number;
    onSave: () => Promise<void>;
    disabled?: boolean;
}

const SaveableField = ({
    children,
    originalValue,
    currentValue,
    onSave,
    disabled = false,
}: SaveableFieldProps) => {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isDirty = String(currentValue) !== String(originalValue);

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);
            await onSave();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>{children}</Box>
                {isDirty && (
                    <Button
                        onClick={handleSave}
                        disabled={saving || disabled}
                        variant="contained"
                        color="primary"
                        size="small"
                        sx={{
                            borderRadius: 0,
                            border: '2px solid #6366f1',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 2,
                            py: 1,
                            minWidth: 80,
                            mt: 1,
                        }}
                    >
                        {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                    </Button>
                )}
            </Box>
            {error && (
                <Alert
                    severity="error"
                    onClose={() => setError(null)}
                    sx={{
                        mt: 1,
                        borderRadius: 0,
                        border: '2px solid #ef4444',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        fontWeight: 600,
                        '& .MuiAlert-icon': {
                            color: '#ef4444',
                        },
                    }}
                >
                    {error}
                </Alert>
            )}
        </Box>
    );
};

export default SaveableField;
